/**
 * Market coverage for the /apps/finance map + exchange grid.
 *
 * EXCHANGES — the major venues the terminal connects to, each with a city
 * lng/lat (for the map marker) and its own domain (for a self-hosted favicon,
 * fetched by scripts/fetch-exchange-icons.ps1). Exchanges are markets, not data
 * vendors, so they're fine to name. Grouped by region for the logo grid.
 *
 * COVERED_COUNTRIES — ISO 3166-1 alpha-2 of the markets we price (the ~50+
 * countries highlighted brighter on the world map). Matches the ISO codes in
 * public/world-countries.geo.json.
 */

export type Exchange = {
  name: string;   // display name
  short: string;  // mono badge / marker label
  city: string;
  country: string; // ISO2
  lng: number;
  lat: number;
  domain: string;  // for the favicon
};

export const EXCHANGE_REGIONS: { region: string; blurb: string; exchanges: Exchange[] }[] = [
  {
    region: "Americas",
    blurb: "Deep North-American liquidity plus Latin America's largest venue.",
    exchanges: [
      { name: "NYSE", short: "NYSE", city: "New York", country: "US", lng: -74.011, lat: 40.707, domain: "nyse.com" },
      { name: "Nasdaq", short: "NSDQ", city: "New York", country: "US", lng: -73.987, lat: 40.756, domain: "nasdaq.com" },
      { name: "TSX", short: "TSX", city: "Toronto", country: "CA", lng: -79.382, lat: 43.648, domain: "tsx.com" },
      { name: "B3", short: "B3", city: "São Paulo", country: "BR", lng: -46.633, lat: -23.551, domain: "b3.com.br" },
    ],
  },
  {
    region: "Europe",
    blurb: "The UK, the euro-zone's core venues, Switzerland and the Nordics.",
    exchanges: [
      { name: "London Stock Exchange", short: "LSE", city: "London", country: "GB", lng: -0.087, lat: 51.514, domain: "londonstockexchange.com" },
      { name: "Euronext", short: "ENX", city: "Paris", country: "FR", lng: 2.341, lat: 48.869, domain: "euronext.com" },
      { name: "Deutsche Börse · XETRA", short: "XETR", city: "Frankfurt", country: "DE", lng: 8.671, lat: 50.114, domain: "deutsche-boerse.com" },
      { name: "SIX Swiss Exchange", short: "SIX", city: "Zürich", country: "CH", lng: 8.540, lat: 47.366, domain: "six-group.com" },
      { name: "Borsa Italiana", short: "MIL", city: "Milan", country: "IT", lng: 9.190, lat: 45.465, domain: "borsaitaliana.it" },
      { name: "BME", short: "BME", city: "Madrid", country: "ES", lng: -3.692, lat: 40.417, domain: "bolsasymercados.es" },
      { name: "Nasdaq Nordic", short: "OMX", city: "Stockholm", country: "SE", lng: 18.063, lat: 59.331, domain: "nasdaqomxnordic.com" },
    ],
  },
  {
    region: "Asia-Pacific",
    blurb: "Tokyo, Greater China, India, Korea, Singapore and Australia.",
    exchanges: [
      { name: "Japan Exchange · Tokyo", short: "JPX", city: "Tokyo", country: "JP", lng: 139.692, lat: 35.681, domain: "jpx.co.jp" },
      { name: "HKEX", short: "HKEX", city: "Hong Kong", country: "HK", lng: 114.158, lat: 22.282, domain: "hkex.com.hk" },
      { name: "Shanghai Stock Exchange", short: "SSE", city: "Shanghai", country: "CN", lng: 121.474, lat: 31.230, domain: "sse.com.cn" },
      { name: "Shenzhen Stock Exchange", short: "SZSE", city: "Shenzhen", country: "CN", lng: 114.058, lat: 22.543, domain: "szse.cn" },
      { name: "NSE India", short: "NSE", city: "Mumbai", country: "IN", lng: 72.866, lat: 19.066, domain: "nseindia.com" },
      { name: "BSE", short: "BSE", city: "Mumbai", country: "IN", lng: 72.833, lat: 18.930, domain: "bseindia.com" },
      { name: "Korea Exchange", short: "KRX", city: "Seoul", country: "KR", lng: 126.978, lat: 37.566, domain: "krx.co.kr" },
      { name: "Taiwan Stock Exchange", short: "TWSE", city: "Taipei", country: "TW", lng: 121.564, lat: 25.034, domain: "twse.com.tw" },
      { name: "SGX", short: "SGX", city: "Singapore", country: "SG", lng: 103.851, lat: 1.290, domain: "sgx.com" },
      { name: "ASX", short: "ASX", city: "Sydney", country: "AU", lng: 151.207, lat: -33.868, domain: "asx.com.au" },
    ],
  },
  {
    region: "Middle East & Africa",
    blurb: "The Gulf's largest exchange and Africa's deepest market.",
    exchanges: [
      { name: "Saudi Exchange · Tadawul", short: "TADAWUL", city: "Riyadh", country: "SA", lng: 46.675, lat: 24.713, domain: "saudiexchange.sa" },
      { name: "Johannesburg Stock Exchange", short: "JSE", city: "Johannesburg", country: "ZA", lng: 28.047, lat: -26.205, domain: "jse.co.za" },
    ],
  },
];

export const EXCHANGES: Exchange[] = EXCHANGE_REGIONS.flatMap((r) => r.exchanges);

// Countries the terminal prices (ISO 3166-1 alpha-2) — highlighted on the map.
export const COVERED_COUNTRIES: string[] = [
  // Americas
  "US", "CA", "MX", "BR", "AR", "CL", "CO", "PE",
  // Europe
  "GB", "IE", "FR", "DE", "NL", "BE", "LU", "CH", "AT", "IT", "ES", "PT",
  "SE", "NO", "DK", "FI", "IS", "PL", "CZ", "HU", "GR", "TR",
  // Asia-Pacific
  "JP", "HK", "CN", "IN", "KR", "TW", "SG", "MY", "TH", "ID", "PH", "VN",
  "AU", "NZ", "PK",
  // Middle East & Africa
  "SA", "AE", "QA", "KW", "IL", "EG", "ZA", "NG", "MA",
];

export const EXCHANGE_COUNT = EXCHANGES.length;
export const MARKET_STATS = {
  exchanges: "30+",
  countries: "50+",
  instruments: "100,000+",
};


/* ────────────────────────────────────────────────────────────────────────────
   TERMINAL PRODUCT CLAIMS — tier 3 (verified constant), not telemetry.

   These describe the terminal's own coverage and have no repo-side or live
   source to derive from, so they are dated constants rather than pretending to
   be live. They were previously typed into five separate places
   (app/apps/finance/page.tsx x4, finance-features-alternating.tsx x2,
   finance-tour-player.tsx), which is five chances to update four of them.

   VERIFIED 2026-09-05. Re-check before any campaign that leans on them, and
   change them HERE — never in a page.
   ──────────────────────────────────────────────────────────────────────────── */

/** Tradeable instruments across all covered markets. */
export const INSTRUMENT_CLAIM = "100,000+";

/** Technical indicators available on the chart. */
export const INDICATOR_CLAIM = "30+";

/** Countries priced, as a floor: COVERED_COUNTRIES rounded down to the nearest ten.
 *  Derived, unlike the two above — the coverage list is in this file. */
export const COUNTRY_FLOOR = `${Math.floor(COVERED_COUNTRIES.length / 10) * 10}+`;
